import React, { useState } from 'react';
import { WebSocketAdapter } from '../../utils/WebSocketAdapter';
import '../../App.css';
import { AppHeader, AvatarSelector , HeaderIcons } from '../shared';
import Footer from '../shared/Footer';
import { avatars } from '../../assets/avatars';
import { useToast } from '../shared/ToastContext';
import RoomSessionStorage from '../../utils/sessionStorage';
import { getRoomCodeFromUrl as getRoomCodeFromUrlUtil, formatRoomCode, clearQueryParamFromUrl } from '../../utils/roomCode';
import { JOIN_ROOM_ERROR_EVENT, isJoinRoomNotFoundMessage } from '../../utils/joinRoomEvents';
import { useTranslation } from '../../i18n';
import { JOIN_ROOM_NOT_FOUND_MESSAGE, translateBackendError } from '../../i18n/helpers';

interface JoinRoomProps {
  socket: WebSocketAdapter;
  onJoinSuccess: () => void;
  onBack: () => void;
  isPremium?: boolean;
}

const JoinRoom: React.FC<JoinRoomProps> = ({ socket, onJoinSuccess, onBack, isPremium = false }) => {
  const { t } = useTranslation();
  const { showToast } = useToast();

  const getRoomCodeFromUrl = () => {
    const roomFromUrl = getRoomCodeFromUrlUtil();
    if (roomFromUrl) {
      return formatRoomCode(roomFromUrl);
    }
    return '';
  };

  const urlRoomCodeInitial = getRoomCodeFromUrl();
  const [roomCode, setRoomCode] = useState(urlRoomCodeInitial);
  const [playerName, setPlayerName] = useState(() => RoomSessionStorage.getPlayerName() || '');
  const [selectedAvatar, setSelectedAvatar] = useState(avatars[0]);
  const [isJoining, setIsJoining] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);
  const hasAutoJoinedRef = React.useRef(false);
  const roomCodeFromUrlRef = React.useRef<string | null>(null);

  const premiumWord = t('lobby.join.premiumWord');
  const premiumSubtitle = t('lobby.join.premiumSubtitle', { premiumWord });
  const [premiumSubtitleBefore, premiumSubtitleAfter] = premiumSubtitle.split(premiumWord);

  React.useEffect(() => {
    if (isPremium) {
      const savedData = RoomSessionStorage.getSavedRoomData();
      const isNonPremiumRoom = savedData.roomName && !savedData.expiresAt;
      if (isNonPremiumRoom) {
        console.log(`Clearing old non-premium room data when entering premium join screen`);
        RoomSessionStorage.clearRoomData();
        RoomSessionStorage.clearRoomExpiresAt();
      }
    }

    const urlRoomCode = getRoomCodeFromUrl();
    if (urlRoomCode) {
      const normalized = urlRoomCode.trim().toUpperCase().slice(0, 4);
      setRoomCode(urlRoomCode);
      if (normalized.length === 4) {
        roomCodeFromUrlRef.current = normalized;
      }
      clearQueryParamFromUrl('room');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (isPremium || isJoining || hasAutoJoinedRef.current) return;
    const codeFromUrl = roomCodeFromUrlRef.current?.trim().toUpperCase().slice(0, 4);
    const nameFromStorage = RoomSessionStorage.getPlayerName()?.trim();
    if (codeFromUrl?.length === 4 && nameFromStorage && socket?.connected) {
      hasAutoJoinedRef.current = true;
      roomCodeFromUrlRef.current = null;
      setIsJoining(true);
      RoomSessionStorage.setRoomName(codeFromUrl);
      RoomSessionStorage.setPlayerName(nameFromStorage);
      RoomSessionStorage.setIsHost(false);
      RoomSessionStorage.setAvatar(selectedAvatar);
      socket.emit('join-room', {
        roomName: codeFromUrl,
        playerName: nameFromStorage,
        avatar: selectedAvatar,
      });
    }
  }, [socket, socket?.connected, isPremium, isJoining, selectedAvatar]);

  const handleJoinRoom = () => {
    setJoinError(null);
    if (!isPremium && !playerName.trim()) {
      showToast(t('toasts.enterName'), 'warning');
      return;
    }
    if (!roomCode.trim()) {
      showToast(t('toasts.enterRoomCode'), 'warning');
      return;
    }

    const roomCodeUpper = roomCode.trim().toUpperCase().slice(0, 4);
    if (roomCodeUpper.length !== 4) {
      showToast(t('toasts.roomCodeLength'), 'warning');
      return;
    }

    if (isPremium) {
      window.location.href = `/?host=${roomCodeUpper}`;
      return;
    }

    if (!socket) {
      showToast(t('toasts.notConnected'), 'error');
      return;
    }

    setIsJoining(true);

    const savedData = RoomSessionStorage.getSavedRoomData();
    const isSavedRoomExpired = savedData.expiresAt ? Date.now() > savedData.expiresAt : false;
    const wasOldRoomNonPremium = savedData.roomName && !savedData.expiresAt && savedData.roomName !== roomCodeUpper;
    if (savedData.roomName && (savedData.roomName !== roomCodeUpper || isSavedRoomExpired || wasOldRoomNonPremium)) {
      console.log(`Clearing saved room data: room mismatch, expired, or switching from non-premium (typed: ${roomCodeUpper}, saved: ${savedData.roomName})`);
      RoomSessionStorage.clearRoomData();
      RoomSessionStorage.clearRoomExpiresAt();
    }

    RoomSessionStorage.setRoomName(roomCodeUpper);
    RoomSessionStorage.setPlayerName(playerName.trim());
    RoomSessionStorage.setAvatar(selectedAvatar);
    RoomSessionStorage.setIsHost(false);

    socket.emit('join-room', {
      roomName: roomCodeUpper,
      playerName: playerName.trim(),
      avatar: selectedAvatar,
    });
  };

  React.useEffect(() => {
    if (!socket) return;

    const handleRoomJoined = () => {
      setIsJoining(false);
      setJoinError(null);
      onJoinSuccess();
    };

    const handleRoomError = (error: { message?: string }) => {
      setIsJoining(false);
      const message = error?.message || '';
      if (isJoinRoomNotFoundMessage(message)) {
        setJoinError(JOIN_ROOM_NOT_FOUND_MESSAGE());
        return;
      }
      const isDuplicateName =
        message.includes('name') &&
        (message.includes('already') || message.includes('choose another name'));
      if (isDuplicateName) {
        setJoinError(t('lobby.join.duplicateName'));
        return;
      }
      if (message) {
        setJoinError(translateBackendError(message));
      }
    };

    const handleJoinRoomErrorEvent = (event: Event) => {
      const customEvent = event as CustomEvent<{ message?: string }>;
      setIsJoining(false);
      if (customEvent.detail?.message) {
        const message = customEvent.detail.message;
        if (isJoinRoomNotFoundMessage(message)) {
          setJoinError(JOIN_ROOM_NOT_FOUND_MESSAGE());
        } else {
          setJoinError(translateBackendError(message));
        }
      }
    };

    socket.on('room-joined', handleRoomJoined);
    socket.on('room-error', handleRoomError);
    window.addEventListener(JOIN_ROOM_ERROR_EVENT, handleJoinRoomErrorEvent);

    return () => {
      socket.off('room-joined', handleRoomJoined);
      socket.off('room-error', handleRoomError);
      window.removeEventListener(JOIN_ROOM_ERROR_EVENT, handleJoinRoomErrorEvent);
    };
  }, [socket, onJoinSuccess, t]);

  const handleGoHome = () => {
    RoomSessionStorage.clearRoomData();
    RoomSessionStorage.clearRoomExpiresAt();
    onBack();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleJoinRoom();
    }
  };

  const handleRoomCodeChange = (value: string) => {
    setRoomCode(formatRoomCode(value));
    if (joinError) setJoinError(null);
  };

  const handleRoomCodePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    handleRoomCodeChange(e.clipboardData.getData('text'));
  };

  return (
    <div className="app" style={{ position: 'relative', paddingBottom: '100px' }}>
      <AppHeader
        logo={null}
        leftButton={{
          icon: HeaderIcons.back,
          onClick: handleGoHome,
          tooltip: t('common.backToHome')
        }}
      />
      <div className="container player-content-wrapper" style={{ marginTop: '5px', marginBottom: '100px' }}>
        {isPremium ? (
          <p className="subtitle join-room-subtitle">
            {premiumSubtitleBefore}
            <span style={{ color: 'var(--color-primary)', fontWeight: '600' }}>{premiumWord}</span>
            {premiumSubtitleAfter}
          </p>
        ) : (
          <p className="subtitle join-room-subtitle">{t('lobby.join.subtitle')}</p>
        )}

        <div style={{ width: '100%', maxWidth: '100%' }}>
          {joinError && (
            <div
              role="alert"
              className="join-room-error"
              style={{
                marginBottom: '1rem',
                padding: '0.75rem 1rem',
                borderRadius: '8px',
                backgroundColor: 'rgba(239, 68, 68, 0.12)',
                border: '1px solid rgba(239, 68, 68, 0.35)',
                color: 'var(--text-primary)',
                fontSize: '0.95rem',
                lineHeight: 1.4,
              }}
            >
              {joinError}
            </div>
          )}
          <label className="form-label">
            {t('common.roomCode')}
          </label>
          <input
            type="text"
            className="input join-room-code-input"
            placeholder={t('common.roomCodePlaceholder')}
            value={roomCode}
            onChange={(e) => handleRoomCodeChange(e.target.value)}
            onPaste={handleRoomCodePaste}
            onKeyDown={handleKeyDown}
            maxLength={4}
            style={{
              fontSize: '1.8rem',
              letterSpacing: '0.5rem',
              textAlign: 'center',
              fontWeight: 'bold',
              textTransform: 'uppercase',
            }}
            disabled={isJoining}
          />

          {!isPremium && (
            <>
              <label className="form-label form-label--spaced join-room-label">
                {t('common.yourName')}
              </label>
              <input
                type="text"
                className="input"
                placeholder={t('common.enterYourName')}
                value={playerName}
                onChange={(e) => {
                  setPlayerName(e.target.value);
                  if (joinError) setJoinError(null);
                }}
                onKeyDown={handleKeyDown}
                style={{
                  fontSize: '1rem',
                }}
                disabled={isJoining}
              />

              <AvatarSelector
                selectedAvatar={selectedAvatar}
                onSelect={(avatar) => {
                  setSelectedAvatar(avatar);
                  if (joinError) setJoinError(null);
                }}
                disabled={isJoining}
              />
            </>
          )}
        </div>
      </div>
      <Footer>
        <div className="join-room-buttons">
          <button
            className="button join-room-back-button"
            onClick={onBack}
            disabled={isJoining}
          >
            {t('common.back')}
          </button>
          <button
            className="button join-room-button"
            onClick={handleJoinRoom}
            disabled={isJoining}
          >
            {isPremium ? t('common.continue') : (isJoining ? t('common.joining') : t('lobby.join.joinRoom'))}
          </button>
        </div>
      </Footer>
    </div>
  );
};

export default JoinRoom;
